import { useState, useEffect } from 'react';
import CourseDatePicker from './CourseDatePicker';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;


// STUDENT_MEETING_CONTEXT_DATE_PICKER_V1

function emptyMeetingCalendarContext() {
  return {
    weeks: [],
    assignments: [],
    typeSettings: {},
    scheduleItems: [],
    scheduleReadings: [],
    scheduleEvents: [],
    holidays: []
  };
}


function meetingCalendarHeaders() {
  return {
    'apikey':
      SUPABASE_KEY,

    'Authorization':
      'Bearer ' +
      SUPABASE_KEY
  };
}


async function readMeetingCalendarJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      label +
      ' failed (' +
      response.status +
      '): ' +
      text
    );
  }

  if (!text) {
    return [];
  }

  return JSON.parse(text);
}


async function fetchMeetingCalendarRows(
  url,
  label
) {
  const response =
    await fetch(
      url,
      {
        headers:
          meetingCalendarHeaders()
      }
    );

  const rows =
    await readMeetingCalendarJson(
      response,
      label
    );

  return Array.isArray(rows)
    ? rows
    : [];
}


async function fetchMeetingCalendarHolidays(
  instructorId
) {
  if (!instructorId) {
    return [];
  }

  const instructors =
    await fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/instructors?id=eq.' +
        encodeURIComponent(
          instructorId
        ) +
        '&select=institution_id&limit=1',
      'Loading instructor institution'
    );

  const institutionId =
    instructors?.[0]
      ?.institution_id;

  if (!institutionId) {
    return [];
  }

  return fetchMeetingCalendarRows(
    SUPABASE_URL +
      '/rest/v1/holidays?institution_id=eq.' +
      encodeURIComponent(
        institutionId
      ) +
      '&or=(is_personal.eq.false,instructor_id.eq.' +
      encodeURIComponent(
        instructorId
      ) +
      ')' +
      '&select=*&order=date.asc',
    'Loading calendar holidays'
  );
}


async function fetchMeetingCalendarContext(
  courseId,
  instructorId
) {
  if (!courseId) {
    return emptyMeetingCalendarContext();
  }

  const encodedCourseId =
    encodeURIComponent(
      courseId
    );

  const [
    weeks,
    assignments,
    settings,
    scheduleReadings,
    scheduleEvents,
    scheduleItems,
    holidays
  ] = await Promise.all([
    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_weeks?course_id=eq.' +
        encodedCourseId +
        '&select=*&order=week_number.asc',
      'Loading course weeks'
    ),

    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_assignments?course_id=eq.' +
        encodedCourseId +
        '&select=*,' +
        'assignment_types(' +
          'id,' +
          'name,' +
          'requires_submission,' +
          'instructions,' +
          'allow_groups,' +
          'is_core,' +
          'assignment_kind,' +
          'is_course_copy,' +
          'source_assignment_type_id,' +
          'course_copy_index' +
        ')' +
        '&order=instance_number.asc',
      'Loading course assignments'
    ),

    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_assignment_type_settings?course_id=eq.' +
        encodedCourseId +
        '&select=*',
      'Loading assignment type settings'
    ),

    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_week_reading_assignments?' +
        'select=' +
          'id,' +
          'course_week_id,' +
          'display_order,' +
          'course_readings(id,title,author),' +
          'course_weeks!inner(' +
            'id,' +
            'course_id,' +
            'week_number,' +
            'class_date' +
          ')' +
        '&course_weeks.course_id=eq.' +
        encodedCourseId,
      'Loading scheduled readings'
    ),

    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_events?' +
        'select=' +
          'id,' +
          'course_week_id,' +
          'title,' +
          'event_date,' +
          'event_time,' +
          'location,' +
          'course_weeks!inner(' +
            'id,' +
            'course_id,' +
            'week_number,' +
            'class_date' +
          ')' +
        '&course_weeks.course_id=eq.' +
        encodedCourseId,
      'Loading course events'
    ),

    fetchMeetingCalendarRows(
      SUPABASE_URL +
        '/rest/v1/course_assignment_schedule_items?' +
        'select=*,' +
        'course_assignments!inner(course_id)' +
        '&course_assignments.course_id=eq.' +
        encodedCourseId +
        '&order=display_order.asc,created_at.asc',
      'Loading assignment schedule'
    ),

    fetchMeetingCalendarHolidays(
      instructorId
    )
  ]);

  const typeSettings = {};

  settings.forEach(
    row => {
      const assignmentTypeId =
        String(
          row?.assignment_type_id ||
          ''
        );

      if (assignmentTypeId) {
        typeSettings[
          assignmentTypeId
        ] = row;
      }
    }
  );

  return {
    weeks,
    assignments,
    typeSettings,
    scheduleItems,
    scheduleReadings,
    scheduleEvents,
    holidays
  };
}

async function fetchMeetings(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?course_id=eq.${courseId}&select=*,student_meeting_slots(*)&order=meeting_date.asc`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  return response.json();
}

async function fetchStudents(courseId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/enrolments?course_id=eq.${courseId}&active=eq.true&select=student_id,students(student_code,english_name,chinese_name,is_master)`,
    {
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
  const rows = await response.json();

  return (Array.isArray(rows) ? rows : [])
    .filter(
      enrolment =>
        enrolment?.students?.is_master !== true
    );
}

async function fetchCourseInstructors(
  courseId
) {
  const [
    courseResponse,
    accessResponse
  ] = await Promise.all([
    fetch(
      `${SUPABASE_URL}/rest/v1/courses?id=eq.${courseId}&select=instructor_id&limit=1`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY
        }
      }
    ),

    fetch(
      `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${courseId}&select=instructor_id`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY
        }
      }
    )
  ]);

  const [
    courseRows,
    accessRows
  ] = await Promise.all([
    courseResponse.json(),
    accessResponse.json()
  ]);

  const primaryId =
    courseRows?.[0]?.instructor_id ||
    null;

  const instructorIds = [
    primaryId,
    ...(
      Array.isArray(accessRows)
        ? accessRows.map(
            row => row.instructor_id
          )
        : []
    )
  ]
    .filter(Boolean)
    .filter(
      (id, index, values) =>
        values.indexOf(id) === index
    );

  if (!instructorIds.length) {
    return [];
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/instructors?id=in.(${instructorIds.join(',')})&select=id,name,email`,
      {
        headers: {
          'apikey': SUPABASE_KEY,
          'Authorization':
            'Bearer ' + SUPABASE_KEY
        }
      }
    );

  const rows =
    await response.json();

  const byId =
    new Map(
      (
        Array.isArray(rows)
          ? rows
          : []
      ).map(
        instructor => [
          instructor.id,
          instructor
        ]
      )
    );

  const primary =
    primaryId
      ? byId.get(primaryId)
      : null;

  const secondary =
    instructorIds
      .filter(
        id => id !== primaryId
      )
      .map(
        id => byId.get(id)
      )
      .filter(Boolean)
      .sort(
        (a, b) =>
          String(
            a.name ||
            a.email ||
            ''
          ).localeCompare(
            String(
              b.name ||
              b.email ||
              ''
            )
          )
      );

  return [
    primary,
    ...secondary
  ].filter(Boolean);
}


async function saveMeeting(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(data)
    }
  );
  return response.json();
}

async function updateMeeting(id, data) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );
}

async function deleteMeeting(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_meetings?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
}

async function saveSlots(slots) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_meeting_slots`,
    {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(slots)
    }
  );
}

async function updateSlot(id, data) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_meeting_slots?id=eq.${id}`,
    {
      method: 'PATCH',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  );
}

async function deleteSlot(id) {
  await fetch(
    `${SUPABASE_URL}/rest/v1/student_meeting_slots?id=eq.${id}`,
    {
      method: 'DELETE',
      headers: {
        'apikey': SUPABASE_KEY,
        'Authorization': 'Bearer ' + SUPABASE_KEY
      }
    }
  );
}

function parseSlotInput(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  const slots = [];
  const errors = [];

  lines.forEach((line, i) => {
    const parts = line.split(/\s+/);
    const time = parts[0];
    const studentCode = parts[1] || null;

    if (!/^\d{1,2}:\d{2}$/.test(time)) {
      errors.push(`Line ${i + 1}: "${line}" — invalid time format. Use HH:MM.`);
      return;
    }

    slots.push({ time, studentCode });
  });

  return { slots, errors };
}

export default function CourseStudentMeetings({ course }) {
  const [meetings, setMeetings] = useState([]);
  const [students, setStudents] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [
    calendarContext,
    setCalendarContext
  ] = useState(
    () =>
      emptyMeetingCalendarContext()
  );

  // New meeting form
  const [newType, setNewType] = useState('Mid-Term');
  const [newDate, setNewDate] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newInstructorId, setNewInstructorId] = useState('');

  // Slot entry — keyed by meeting id
  const [slotText, setSlotText] = useState({});
  const [slotErrors, setSlotErrors] = useState({});
  const [slotSaving, setSlotSaving] = useState({});
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);

  // STUDENT_MEETING_ADD_FROM_LIST_V1

  /*
   * Each meeting remembers its own slot-entry mode and
   * Add-from-list selection state.
   */
  const [
    slotAddMode,
    setSlotAddMode
  ] = useState({});

  const [
    slotSelectedStudentIds,
    setSlotSelectedStudentIds
  ] = useState({});

  const [
    slotStudentTimes,
    setSlotStudentTimes
  ] = useState({});

  const [
    slotListSearch,
    setSlotListSearch
  ] = useState({});

  useEffect(() => {
    Promise.all([
      fetchMeetings(course.id),
      fetchStudents(course.id),
      fetchCourseInstructors(course.id),

      fetchMeetingCalendarContext(
        course.id,
        course.instructor_id
      ).catch(err => {
        /*
         * Calendar context must not prevent the
         * Student Meetings page itself from loading.
         */
        console.error(
          'Student meeting calendar context failed:',
          err
        );

        return emptyMeetingCalendarContext();
      })
    ])
      .then(([
        meetingRows,
        studentRows,
        instructorRows,
        loadedCalendarContext
      ]) => {
        setMeetings(
          Array.isArray(meetingRows)
            ? meetingRows
            : []
        );

        setStudents(
          Array.isArray(studentRows)
            ? studentRows
            : []
        );

        setCalendarContext(
          loadedCalendarContext ||
          emptyMeetingCalendarContext()
        );

        const loadedInstructors =
          Array.isArray(instructorRows)
            ? instructorRows
            : [];

        setInstructors(
          loadedInstructors
        );

        setNewInstructorId(
          loadedInstructors[0]?.id ||
          course.instructor_id ||
          ''
        );
      })
      .finally(
        () => setLoading(false)
      );
  }, [
    course.id,
    course.instructor_id
  ]);

  async function handleAddMeeting(e) {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!newDate) {
      return setError(
        'Select a meeting date.'
      );
    }

    if (
      instructors.length > 1 &&
      !newInstructorId
    ) {
      return setError(
        'Select an instructor.'
      );
    }

    setSaving(true);
    try {
      const result = await saveMeeting({
        course_id: course.id,
        meeting_type: newType,
        meeting_date: newDate,
        instructor_id:
          newInstructorId ||
          instructors[0]?.id ||
          course.instructor_id ||
          null,
        location:
          newLocation.trim() ||
          null
      });
      const newMeeting = { ...result[0], student_meeting_slots: [] };
      setMeetings(prev => [...prev, newMeeting].sort((a, b) =>
        a.meeting_date.localeCompare(b.meeting_date)
      ));
      setExpandedMeetingId(newMeeting.id);
      setNewDate('');
      setNewLocation('');
      setSuccess('Meeting created. Now add time slots below.');
    } catch (err) {
      setError('Failed to create meeting.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteMeeting(meetingId) {
    if (!window.confirm('Delete this meeting and all its slots?')) return;
    await deleteMeeting(meetingId);
    setMeetings(prev => prev.filter(m => m.id !== meetingId));
    setSuccess('Meeting deleted.');
  }

  async function handleAddSlots(meetingId) {
    setSlotErrors(prev => ({ ...prev, [meetingId]: [] }));
    const text = slotText[meetingId] || '';
    if (!text.trim()) return;

    const { slots, errors } = parseSlotInput(text);
    if (errors.length) {
      setSlotErrors(prev => ({ ...prev, [meetingId]: errors }));
      return;
    }

    setSlotSaving(prev => ({ ...prev, [meetingId]: true }));
    try {
      const slotRows = slots.map(s => ({
        meeting_id: meetingId,
        meeting_time: s.time,
        student_id: s.studentCode
          ? (students.find(st => st.students?.student_code === s.studentCode)?.student_id || null)
          : null
      }));
      await saveSlots(slotRows);
      const updated = await fetchMeetings(course.id);
      setMeetings(updated);
      setSlotText(prev => ({ ...prev, [meetingId]: '' }));
      setSuccess('Slots added.');
    } catch (err) {
      setSlotErrors(prev => ({ ...prev, [meetingId]: ['Failed to save slots.'] }));
    } finally {
      setSlotSaving(prev => ({ ...prev, [meetingId]: false }));
    }
  }

  function setMeetingSlotAddMode(
    meetingId,
    mode
  ) {
    setSlotAddMode(
      prev => ({
        ...prev,
        [meetingId]:
          mode
      })
    );

    setSlotErrors(
      prev => ({
        ...prev,
        [meetingId]:
          []
      })
    );
  }


  function toggleMeetingStudent(
    meetingId,
    studentId
  ) {
    setSlotSelectedStudentIds(
      prev => {
        const current =
          prev[meetingId] || [];

        const selected =
          current.includes(
            studentId
          );

        return {
          ...prev,

          [meetingId]:
            selected
              ? current.filter(
                  id =>
                    id !==
                    studentId
                )
              : [
                  ...current,
                  studentId
                ]
        };
      }
    );
  }


  function setMeetingStudentTime(
    meetingId,
    studentId,
    value
  ) {
    setSlotStudentTimes(
      prev => ({
        ...prev,

        [meetingId]: {
          ...(
            prev[meetingId] ||
            {}
          ),

          [studentId]:
            value
        }
      })
    );
  }


  async function handleAddSelectedStudents(
    meetingId
  ) {
    setSlotErrors(
      prev => ({
        ...prev,
        [meetingId]:
          []
      })
    );

    const selectedIds =
      slotSelectedStudentIds[
        meetingId
      ] || [];

    if (!selectedIds.length) {
      setSlotErrors(
        prev => ({
          ...prev,
          [meetingId]: [
            'Select at least one student.'
          ]
        })
      );

      return;
    }

    const meeting =
      meetings.find(
        item =>
          item.id ===
          meetingId
      );

    if (!meeting) {
      return;
    }

// STUDENT_MEETING_TYPE_WIDE_STUDENT_EXCLUSION_V1

    /*
     * A student may appear only once for each meeting type
     * across the whole course.
     *
     * Example:
     * - assigned to one Mid-Term meeting -> unavailable for all
     *   other Mid-Term meetings
     * - still available for a Final meeting
     */
    const sameTypeAssignedStudentIds =
      new Set(
        meetings
          .filter(
            candidate =>
              String(
                candidate
                  ?.meeting_type ||
                ''
              ) ===
              String(
                meeting
                  ?.meeting_type ||
                ''
              )
          )
          .flatMap(
            candidate =>
              candidate
                ?.student_meeting_slots ||
              []
          )
          .map(
            slot =>
              slot.student_id
                ? String(
                    slot.student_id
                  )
                : ''
          )
          .filter(Boolean)
      );

    const eligibleIds =
      selectedIds.filter(
        studentId =>
          !sameTypeAssignedStudentIds.has(
            String(
              studentId
            )
          )
      );

    if (!eligibleIds.length) {
      setSlotErrors(
        prev => ({
          ...prev,
          [meetingId]: [
            'Those students are already in this meeting.'
          ]
        })
      );

      return;
    }

    const times =
      slotStudentTimes[
        meetingId
      ] || {};

    const missingTimes =
      eligibleIds.filter(
        studentId =>
          !String(
            times[
              studentId
            ] || ''
          ).trim()
      );

    if (missingTimes.length) {
      setSlotErrors(
        prev => ({
          ...prev,
          [meetingId]: [
            'Enter a meeting time for every selected student.'
          ]
        })
      );

      return;
    }

    const invalidTimes =
      eligibleIds.filter(
        studentId =>
          !/^([01]\d|2[0-3]):[0-5]\d$/.test(
            String(
              times[
                studentId
              ] || ''
            ).trim()
          )
      );

    if (invalidTimes.length) {
      setSlotErrors(
        prev => ({
          ...prev,
          [meetingId]: [
            'One or more meeting times are invalid.'
          ]
        })
      );

      return;
    }

    setSlotSaving(
      prev => ({
        ...prev,
        [meetingId]:
          true
      })
    );

    try {
      const slotRows =
        eligibleIds.map(
          studentId => ({
            meeting_id:
              meetingId,

            meeting_time:
              String(
                times[
                  studentId
                ]
              ).trim(),

            student_id:
              studentId
          })
        );

      await saveSlots(
        slotRows
      );

      const updated =
        await fetchMeetings(
          course.id
        );

      setMeetings(
        Array.isArray(updated)
          ? updated
          : []
      );

      setSlotSelectedStudentIds(
        prev => ({
          ...prev,
          [meetingId]:
            []
        })
      );

      setSlotStudentTimes(
        prev => ({
          ...prev,
          [meetingId]:
            {}
        })
      );

      setSlotListSearch(
        prev => ({
          ...prev,
          [meetingId]:
            ''
        })
      );

      setSuccess(
        eligibleIds.length +
          ' student' +
          (
            eligibleIds.length ===
            1
              ? ''
              : 's'
          ) +
          ' added.'
      );
    } catch (err) {
      console.error(
        'Adding meeting students failed:',
        err
      );

      setSlotErrors(
        prev => ({
          ...prev,
          [meetingId]: [
            'Failed to add students.'
          ]
        })
      );
    } finally {
      setSlotSaving(
        prev => ({
          ...prev,
          [meetingId]:
            false
        })
      );
    }
  }


  async function handleDeleteSlot(meetingId, slotId) {
    await deleteSlot(slotId);
    setMeetings(prev => prev.map(m =>
      m.id === meetingId
        ? { ...m, student_meeting_slots: m.student_meeting_slots.filter(s => s.id !== slotId) }
        : m
    ));
  }

  async function handleUpdateSlotStudent(meetingId, slotId, studentCode) {
    const studentId = studentCode
      ? (students.find(s => s.students?.student_code === studentCode)?.student_id || null)
      : null;
    await updateSlot(slotId, { student_id: studentId });
    setMeetings(prev => prev.map(m =>
      m.id === meetingId
        ? {
            ...m,
            student_meeting_slots: m.student_meeting_slots.map(s =>
              s.id === slotId ? { ...s, student_id: studentId } : s
            )
          }
        : m
    ));
  }

  async function handleUpdateMeetingDate(
    meetingId,
    newDate
  ) {
    await updateMeeting(
      meetingId,
      {
        meeting_date:
          newDate
      }
    );

    setMeetings(
      prev =>
        prev.map(
          meeting =>
            meeting.id === meetingId
              ? {
                  ...meeting,
                  meeting_date:
                    newDate
                }
              : meeting
        )
    );
  }


  async function handleUpdateMeetingInstructor(
    meetingId,
    instructorId
  ) {
    await updateMeeting(
      meetingId,
      {
        instructor_id:
          instructorId ||
          null
      }
    );

    setMeetings(
      prev =>
        prev.map(
          meeting =>
            meeting.id === meetingId
              ? {
                  ...meeting,
                  instructor_id:
                    instructorId ||
                    null
                }
              : meeting
        )
    );

    setSuccess(
      'Meeting instructor saved.'
    );
  }


  function handleMeetingLocationChange(
    meetingId,
    location
  ) {
    setMeetings(
      prev =>
        prev.map(
          meeting =>
            meeting.id === meetingId
              ? {
                  ...meeting,
                  location
                }
              : meeting
        )
    );
  }


  async function handleSaveMeetingLocation(
    meetingId,
    location
  ) {
    await updateMeeting(
      meetingId,
      {
        location:
          String(
            location || ''
          ).trim() ||
          null
      }
    );

    setSuccess(
      'Meeting location saved.'
    );
  }

  if (loading) return <div style={styles.muted}>Loading...</div>;

  return (
    <div>
      <h2 style={styles.subheading}>Student Meetings</h2>
      <p style={styles.muted}>
        Create a meeting by selecting its type, date, and location. Then add time slots — one per line
        in the format <strong>HH:MM student_id</strong>. Student ID is optional and can be
        assigned afterwards using the dropdown.
      </p>

      {error && <div style={styles.error}>{error}</div>}
      {success && <div style={styles.success}>{success}</div>}

      {/* Add meeting form */}
      <form onSubmit={handleAddMeeting} style={styles.addForm}>
        <h3 style={styles.addHeading}>Create Meeting</h3>
        <div style={styles.row}>
          <div style={styles.field}>
            <label style={styles.label}>Meeting type</label>
            <select
              style={styles.input}
              value={newType}
              onChange={e => setNewType(e.target.value)}
            >
              <option value="Mid-Term">Mid-Term</option>
              <option value="Final">Final</option>
            </select>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Date</label>
            <CourseDatePicker
              value={newDate}
              onChange={setNewDate}
              course={course}
              weeks={
                calendarContext.weeks
              }
              assignments={
                calendarContext.assignments
              }
              typeSettings={
                calendarContext.typeSettings
              }
              scheduleItems={
                calendarContext.scheduleItems
              }
              scheduleReadings={
                calendarContext.scheduleReadings
              }
              scheduleEvents={
                calendarContext.scheduleEvents
              }
              holidays={
                calendarContext.holidays
              }
            />
          </div>

          {instructors.length > 1 && (
            <div style={styles.field}>
              <label style={styles.label}>
                Instructor
              </label>

              <select
                style={styles.input}
                value={newInstructorId}
                onChange={e =>
                  setNewInstructorId(
                    e.target.value
                  )
                }
              >
                {instructors.map(
                  instructor => (
                    <option
                      key={instructor.id}
                      value={instructor.id}
                    >
                      {instructor.name ||
                        instructor.email}
                    </option>
                  )
                )}
              </select>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>
              Location
            </label>

            <input
              style={styles.input}
              type="text"
              value={newLocation}
              onChange={e =>
                setNewLocation(
                  e.target.value
                )
              }
              placeholder="e.g. M201"
            />
          </div>
          <div style={styles.fieldBtn}>
            <label style={styles.label}>&nbsp;</label>
            <button style={styles.btn} disabled={saving}>
              {saving ? 'Creating...' : 'Create Meeting'}
            </button>
          </div>
        </div>
      </form>

      {/* Existing meetings */}
      {meetings.length === 0 && (
        <div style={styles.muted}>No meetings created yet.</div>
      )}

      {meetings.map(meeting => {
        const isExpanded = expandedMeetingId === meeting.id;
        const slots = [...(meeting.student_meeting_slots || [])]
          .sort((a, b) => a.meeting_time.localeCompare(b.meeting_time));
        const errs =
          slotErrors[
            meeting.id
          ] || [];

        const assignedInstructor =
          instructors.find(
            instructor =>
              instructor.id ===
              meeting.instructor_id
          ) || null;

        const currentSlotMode =
          slotAddMode[
            meeting.id
          ] || 'line';

        const selectedStudentIds =
          slotSelectedStudentIds[
            meeting.id
          ] || [];

        /*
         * The available-student list is scoped by meeting type,
         * not merely by this individual meeting.
         *
         * A student assigned anywhere under Mid-Term disappears
         * from every other Mid-Term meeting list. The same applies
         * independently to Final meetings.
         */
        const assignedStudentIds =
          new Set(
            meetings
              .filter(
                candidate =>
                  String(
                    candidate
                      ?.meeting_type ||
                    ''
                  ) ===
                  String(
                    meeting
                      ?.meeting_type ||
                    ''
                  )
              )
              .flatMap(
                candidate =>
                  candidate
                    ?.student_meeting_slots ||
                  []
              )
              .map(
                slot =>
                  slot.student_id
                    ? String(
                        slot.student_id
                      )
                    : ''
              )
              .filter(Boolean)
          );

        const listSearch =
          String(
            slotListSearch[
              meeting.id
            ] || ''
          )
            .trim()
            .toLowerCase();

        const availableStudents =
          students
            .filter(
              student =>
                !assignedStudentIds.has(
                  String(
                    student.student_id
                  )
                )
            )
            .filter(
              student => {
                if (!listSearch) {
                  return true;
                }

                return [
                  student.students
                    ?.student_code,

                  student.students
                    ?.english_name,

                  student.students
                    ?.chinese_name
                ]
                  .filter(Boolean)
                  .some(
                    value =>
                      String(value)
                        .toLowerCase()
                        .includes(
                          listSearch
                        )
                  );
              }
            )
            .sort(
              (a, b) =>
                String(
                  a.students
                    ?.student_code ||
                  ''
                ).localeCompare(
                  String(
                    b.students
                      ?.student_code ||
                    ''
                  )
                )
            );

        return (
          <div key={meeting.id} style={styles.meetingCard}>
            <div
              style={styles.meetingHeader}
              role="button"
              tabIndex={0}
              onClick={() =>
                setExpandedMeetingId(
                  isExpanded
                    ? null
                    : meeting.id
                )
              }
              onKeyDown={event => {
                if (
                  event.target !==
                  event.currentTarget
                ) {
                  return;
                }

                if (
                  event.key ===
                    'Enter' ||
                  event.key ===
                    ' '
                ) {
                  event.preventDefault();

                  setExpandedMeetingId(
                    isExpanded
                      ? null
                      : meeting.id
                  );
                }
              }}
            >
              <div style={styles.meetingHeaderLeft}>
                <span style={styles.meetingType}>{meeting.meeting_type}</span>
                <div
                  style={
                    styles.meetingDatePicker
                  }
                  onClick={event =>
                    event.stopPropagation()
                  }
                  onKeyDown={event =>
                    event.stopPropagation()
                  }
                >
                  <CourseDatePicker
                    value={
                      meeting.meeting_date ||
                      ''
                    }
                    onChange={value => {
                      /*
                       * Existing meetings must always
                       * retain a meeting date.
                       */
                      if (!value) {
                        return;
                      }

                      handleUpdateMeetingDate(
                        meeting.id,
                        value
                      );
                    }}
                    course={course}
                    weeks={
                      calendarContext.weeks
                    }
                    assignments={
                      calendarContext.assignments
                    }
                    typeSettings={
                      calendarContext.typeSettings
                    }
                    scheduleItems={
                      calendarContext.scheduleItems
                    }
                    scheduleReadings={
                      calendarContext.scheduleReadings
                    }
                    scheduleEvents={
                      calendarContext.scheduleEvents
                    }
                    holidays={
                      calendarContext.holidays
                    }
                  />
                </div>
                <span style={styles.slotCount}>
                  {slots.length} slot{slots.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={styles.meetingHeaderRight}>
                <button
                  style={styles.dangerBtn}
                  onClick={e => { e.stopPropagation(); handleDeleteMeeting(meeting.id); }}
                >
                  Delete
                </button>
                <span style={styles.chevron}>{isExpanded ? '▾' : '▸'}</span>
              </div>
            </div>

            {isExpanded && (
              <div style={styles.meetingBody}>

                <div
                  style={{
                    ...styles.row,
                    marginBottom: '20px'
                  }}
                >
                  {instructors.length > 1 && (
                    <div style={styles.field}>
                      <label style={styles.label}>
                        Instructor
                      </label>

                      <select
                        style={styles.input}
                        value={
                          meeting.instructor_id ||
                          ''
                        }
                        onChange={e =>
                          handleUpdateMeetingInstructor(
                            meeting.id,
                            e.target.value
                          )
                        }
                      >
                        {instructors.map(
                          instructor => (
                            <option
                              key={instructor.id}
                              value={instructor.id}
                            >
                              {instructor.name ||
                                instructor.email}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                  )}

                  {instructors.length === 1 && (
                    <div style={styles.field}>
                      <label style={styles.label}>
                        Instructor
                      </label>

                      <div
                        style={{
                          ...styles.input,
                          background: '#f7f9fa'
                        }}
                      >
                        {assignedInstructor?.name ||
                          assignedInstructor?.email ||
                          instructors[0]?.name ||
                          instructors[0]?.email ||
                          '—'}
                      </div>
                    </div>
                  )}

                  <div style={styles.field}>
                    <label style={styles.label}>
                      Location
                    </label>

                    <input
                      style={styles.input}
                      type="text"
                      value={
                        meeting.location ||
                        ''
                      }
                      onChange={e =>
                        handleMeetingLocationChange(
                          meeting.id,
                          e.target.value
                        )
                      }
                      onBlur={e =>
                        handleSaveMeetingLocation(
                          meeting.id,
                          e.target.value
                        )
                      }
                      placeholder="e.g. M201"
                    />
                  </div>
                </div>

                {/* Slot entry */}
                <div style={styles.slotEntry}>
                  <div
                    style={
                      styles.slotModePills
                    }
                  >
                    <button
                      type="button"
                      style={{
                        ...styles.slotModePill,

                        ...(
                          currentSlotMode ===
                          'line'
                            ? styles
                                .slotModePillActive
                            : {}
                        )
                      }}
                      onClick={() =>
                        setMeetingSlotAddMode(
                          meeting.id,
                          'line'
                        )
                      }
                    >
                      Add by line
                    </button>

                    <button
                      type="button"
                      style={{
                        ...styles.slotModePill,

                        ...(
                          currentSlotMode ===
                          'list'
                            ? styles
                                .slotModePillActive
                            : {}
                        )
                      }}
                      onClick={() =>
                        setMeetingSlotAddMode(
                          meeting.id,
                          'list'
                        )
                      }
                    >
                      Add from list
                    </button>
                  </div>


                  {currentSlotMode ===
                    'line' && (
                    <>
                      <label
                        style={
                          styles.label
                        }
                      >
                        Add time slots
                        (HH:MM student_id,
                        one per line)
                      </label>

                      <textarea
                        style={
                          styles.textarea
                        }
                        value={
                          slotText[
                            meeting.id
                          ] || ''
                        }
                        onChange={e =>
                          setSlotText(
                            prev => ({
                              ...prev,

                              [meeting.id]:
                                e.target
                                  .value
                            })
                          )
                        }
                        placeholder={
                          '09:00 B11234\n09:20 B11235\n09:40'
                        }
                        rows={5}
                      />

                      {errs.length >
                        0 && (
                        <div
                          style={
                            styles.error
                          }
                        >
                          {errs.map(
                            (
                              error,
                              index
                            ) => (
                              <div
                                key={
                                  index
                                }
                              >
                                {error}
                              </div>
                            )
                          )}
                        </div>
                      )}

                      <button
                        type="button"
                        style={{
                          ...styles.btn,
                          marginTop:
                            '8px'
                        }}
                        disabled={
                          slotSaving[
                            meeting.id
                          ]
                        }
                        onClick={() =>
                          handleAddSlots(
                            meeting.id
                          )
                        }
                      >
                        {
                          slotSaving[
                            meeting.id
                          ]
                            ? 'Adding...'
                            : 'Add Slots'
                        }
                      </button>
                    </>
                  )}


                  {currentSlotMode ===
                    'list' && (
                    <div>
                      <div
                        style={
                          styles
                            .slotListControls
                        }
                      >
                        <input
                          type="text"
                          style={
                            styles
                              .slotListSearch
                          }
                          value={
                            slotListSearch[
                              meeting.id
                            ] || ''
                          }
                          onChange={event =>
                            setSlotListSearch(
                              prev => ({
                                ...prev,

                                [meeting.id]:
                                  event
                                    .target
                                    .value
                              })
                            )
                          }
                          placeholder="Search by student ID or name..."
                        />

                        <div
                          style={
                            styles
                              .slotSelectionMeta
                          }
                        >
                          {
                            selectedStudentIds
                              .length
                          } selected
                        </div>
                      </div>


                      {availableStudents
                        .length ===
                        0 ? (
                        <div
                          style={
                            styles
                              .slotEmptyList
                          }
                        >
                          {students.length
                            ? 'No course students available to add.'
                            : 'No course students found.'}
                        </div>
                      ) : (
                        <div
                          style={
                            styles
                              .slotChecklist
                          }
                        >
                          {availableStudents
                            .map(
                              student => {
                                const studentId =
                                  student
                                    .student_id;

                                const selected =
                                  selectedStudentIds
                                    .includes(
                                      studentId
                                    );

                                return (
                                  <div
                                    key={
                                      studentId
                                    }
                                    style={
                                      styles
                                        .slotChecklistRow
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        selected
                                      }
                                      onChange={() =>
                                        toggleMeetingStudent(
                                          meeting.id,
                                          studentId
                                        )
                                      }
                                    />

                                    <span
                                      style={
                                        styles
                                          .slotChecklistCode
                                      }
                                    >
                                      {
                                        student
                                          .students
                                          ?.student_code ||
                                        '—'
                                      }
                                    </span>

                                    <span
                                      style={
                                        styles
                                          .slotChecklistName
                                      }
                                    >
                                      {
                                        student
                                          .students
                                          ?.english_name ||
                                        student
                                          .students
                                          ?.chinese_name ||
                                        '—'
                                      }
                                    </span>

                                    <input
                                      type="time"
                                      style={
                                        styles
                                          .slotTimeInput
                                      }
                                      disabled={
                                        !selected
                                      }
                                      value={
                                        slotStudentTimes[
                                          meeting.id
                                        ]?.[
                                          studentId
                                        ] || ''
                                      }
                                      onChange={
                                        event =>
                                          setMeetingStudentTime(
                                            meeting.id,
                                            studentId,
                                            event
                                              .target
                                              .value
                                          )
                                      }
                                      aria-label={
                                        'Meeting time for ' +
                                        (
                                          student
                                            .students
                                            ?.english_name ||
                                          student
                                            .students
                                            ?.student_code ||
                                          'student'
                                        )
                                      }
                                    />
                                  </div>
                                );
                              }
                            )}
                        </div>
                      )}


                      {errs.length >
                        0 && (
                        <div
                          style={{
                            ...styles.error,
                            marginTop:
                              '10px'
                          }}
                        >
                          {errs.map(
                            (
                              error,
                              index
                            ) => (
                              <div
                                key={
                                  index
                                }
                              >
                                {error}
                              </div>
                            )
                          )}
                        </div>
                      )}


                      <button
                        type="button"
                        style={{
                          ...styles.btn,
                          marginTop:
                            '12px'
                        }}
                        disabled={
                          slotSaving[
                            meeting.id
                          ] ||
                          selectedStudentIds
                            .length ===
                            0
                        }
                        onClick={() =>
                          handleAddSelectedStudents(
                            meeting.id
                          )
                        }
                      >
                        {
                          slotSaving[
                            meeting.id
                          ]
                            ? 'Adding...'
                            : (
                                'Add Selected' +
                                (
                                  selectedStudentIds
                                    .length
                                    ? ' (' +
                                      selectedStudentIds
                                        .length +
                                      ')'
                                    : ''
                                )
                              )
                        }
                      </button>
                    </div>
                  )}
                </div>

                {/* Slot list */}
                {slots.length > 0 && (
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Time</th>
                        <th style={styles.th}>Student</th>
                        <th style={styles.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {slots.map(slot => {
                        const assignedStudent = students.find(
                          s => s.student_id === slot.student_id
                        );
                        const studentCode = assignedStudent?.students?.student_code || '';

                        return (
                          <tr key={slot.id}>
                            <td style={styles.td}>{slot.meeting_time.slice(0, 5)}</td>
                            <td style={styles.td}>
                              <select
                                style={styles.studentSelect}
                                value={studentCode}
                                onChange={e => handleUpdateSlotStudent(
                                  meeting.id, slot.id, e.target.value
                                )}
                              >
                                <option value="">— Unassigned —</option>
                                {students.map(s => (
                                  <option
                                    key={s.student_id}
                                    value={s.students?.student_code}
                                  >
                                    {s.students?.english_name || s.students?.chinese_name || s.students?.student_code}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={styles.td}>
                              <button
                                style={styles.smallDangerBtn}
                                onClick={() => handleDeleteSlot(meeting.id, slot.id)}
                              >
                                ×
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const styles = {
  subheading: { margin: '0 0 8px', fontSize: '18px', color: '#1f2933' },
  muted: { color: '#66788a', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  error: {
    background: '#faeaea', border: '1px solid #e8bcbc', borderRadius: '8px',
    padding: '10px 14px', color: '#a12a2a', fontSize: '14px', marginBottom: '16px'
  },
  success: {
    background: '#e7f5ed', border: '1px solid #b9dfc9', borderRadius: '8px',
    padding: '10px 14px', color: '#216e45', fontSize: '14px', marginBottom: '16px'
  },
  addForm: {
    border: '1px solid #d7dee5', borderRadius: '10px', padding: '20px',
    background: '#f7f9fa', marginBottom: '24px'
  },
  addHeading: { margin: '0 0 16px', fontSize: '16px', color: '#1f2933' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' },
  field: { flex: 1, minWidth: '160px' },
  fieldBtn: { flexShrink: 0 },
  label: { display: 'block', fontWeight: '700', fontSize: '13px', marginBottom: '6px', color: '#1f2933' },
  input: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box', background: 'white'
  },
  btn: {
    background: '#215b7d', color: 'white', border: 'none', borderRadius: '8px',
    padding: '9px 16px', fontSize: '14px', fontWeight: '600', cursor: 'pointer'
  },
  meetingCard: {
    border: '1px solid #d7dee5', borderRadius: '10px',
    overflow: 'hidden', background: 'white', marginBottom: '12px'
  },
  meetingHeader: {
    width: '100%', display: 'flex', justifyContent: 'space-between',
    alignItems: 'center', padding: '14px 16px', border: 'none',
    background: '#f7f9fa', cursor: 'pointer', borderBottom: '1px solid #d7dee5',
    gap: '12px'
  },
  meetingHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    minWidth: 0
  },
  meetingHeaderRight: { display: 'flex', alignItems: 'center', gap: '10px' },
  meetingType: { fontWeight: '700', fontSize: '14px', color: '#1f2933' },
  dateInput: {
    border: '1px solid #d7dee5', borderRadius: '6px', padding: '4px 8px',
    fontSize: '13px', background: 'white'
  },
  meetingDatePicker: {
    width: '220px',
    minWidth: '180px',
    maxWidth: '100%'
  },
  slotCount: { fontSize: '13px', color: '#66788a' },
  chevron: { color: '#66788a', fontSize: '16px' },
  meetingBody: { padding: '16px' },
  slotEntry: { marginBottom: '20px' },
  textarea: {
    width: '100%', border: '1px solid #d7dee5', borderRadius: '8px',
    padding: '9px 11px', fontSize: '14px', boxSizing: 'border-box',
    background: 'white', fontFamily: 'monospace', resize: 'vertical'
  },
  slotModePills: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginBottom: '14px'
  },
  slotModePill: {
    border: '1px solid #cbd5df',
    borderRadius: '999px',
    background: 'white',
    color: '#526575',
    padding: '6px 11px',
    fontSize: '12px',
    fontWeight: '700',
    cursor: 'pointer'
  },
  slotModePillActive: {
    borderColor: '#215b7d',
    background: '#215b7d',
    color: 'white'
  },
  slotListControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '10px'
  },
  slotListSearch: {
    flex: '1 1 260px',
    minWidth: '180px',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '8px 10px',
    fontSize: '13px',
    background: 'white',
    boxSizing: 'border-box'
  },
  slotSelectionMeta: {
    fontSize: '12px',
    fontWeight: '700',
    color: '#66788a',
    whiteSpace: 'nowrap'
  },
  slotChecklist: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    overflow: 'hidden',
    maxHeight: '320px',
    overflowY: 'auto',
    background: 'white'
  },
  slotChecklistRow: {
    display: 'grid',
    gridTemplateColumns:
      '24px minmax(80px, 110px) minmax(140px, 1fr) 110px',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 10px',
    borderBottom: '1px solid #edf1f4',
    fontSize: '13px'
  },
  slotChecklistCode: {
    color: '#526575',
    fontWeight: '700'
  },
  slotChecklistName: {
    color: '#1f2933',
    minWidth: 0,
    overflowWrap: 'anywhere'
  },
  slotTimeInput: {
    width: '100%',
    border: '1px solid #cbd5df',
    borderRadius: '6px',
    padding: '5px 6px',
    fontSize: '13px',
    background: 'white',
    boxSizing: 'border-box'
  },
  slotEmptyList: {
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '14px',
    color: '#66788a',
    fontSize: '13px',
    background: '#f7f9fa'
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left', padding: '9px 11px', fontSize: '13px',
    color: '#42515f', background: '#f0f4f7', borderBottom: '1px solid #d7dee5'
  },
  td: { padding: '9px 11px', borderBottom: '1px solid #d7dee5', fontSize: '14px' },
  studentSelect: {
    border: '1px solid #d7dee5', borderRadius: '6px', padding: '5px 8px',
    fontSize: '13px', background: 'white', width: '100%'
  },
  dangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '5px 10px', fontSize: '13px', cursor: 'pointer'
  },
  smallDangerBtn: {
    background: 'transparent', color: '#a12a2a', border: '1px solid #e2b8b8',
    borderRadius: '6px', padding: '2px 8px', fontSize: '16px', cursor: 'pointer',
    lineHeight: '1'
  }
};